/**
 * Task detail — inline fields (status, type, due, links, assignee), rich description & activity (newest first).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enAU } from 'date-fns/locale';
import apiService from '../services/api';
import { TaskDetailResponse, TaskMetaResponse } from '../types/task';
import { isQuillNoteEmpty, NOTE_QUILL_MODULES } from '../utils/noteQuill';
import { looksLikeHtml } from '../utils/htmlContent';

type AccountLinkOption = { id: string; display_name: string; sonic_account_number?: string | null };
type CaseLinkOption = { id: string; case_number: string; subject: string };
type TicketLinkOption = { id: string; ticket_number: string; subject?: string | null };

function formatAccountLinkLabel(a: AccountLinkOption) {
  const num = a.sonic_account_number?.trim();
  return num ? `${num} — ${a.display_name || 'Account'}` : a.display_name || a.id;
}

function formatCaseLabel(c: CaseLinkOption) {
  return `${c.case_number} — ${c.subject || 'Case'}`;
}

function formatTicketLabel(t: TicketLinkOption) {
  return `${t.ticket_number} — ${(t.subject || '').trim() || 'Ticket'}`;
}

type LinkTarget = 'none' | 'account' | 'case' | 'ticket';

function linkTargetFromTask(t: TaskDetailResponse): LinkTarget {
  // Case/ticket take precedence: when one is linked the account is derived from
  // it (complementary), so the task should still present as a case/ticket link.
  if (t.case_id) return 'case';
  if (t.support_desk_ticket_id) return 'ticket';
  if (t.account_id) return 'account';
  return 'none';
}

const TaskDetailPageV2: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState<TaskMetaResponse | null>(null);
  const [task, setTask] = useState<TaskDetailResponse | null>(null);
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [inlineSaving, setInlineSaving] = useState<string | null>(null);

  const [titleDraft, setTitleDraft] = useState('');
  const [descHtml, setDescHtml] = useState('');
  const [commentHtml, setCommentHtml] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  const [accountLink, setAccountLink] = useState<AccountLinkOption | null>(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOptions, setAccountOptions] = useState<AccountLinkOption[]>([]);

  const [caseLink, setCaseLink] = useState<CaseLinkOption | null>(null);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseOptions, setCaseOptions] = useState<CaseLinkOption[]>([]);

  const [ticketLink, setTicketLink] = useState<TicketLinkOption | null>(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketOptions, setTicketOptions] = useState<TicketLinkOption[]>([]);

  const [linkTarget, setLinkTarget] = useState<LinkTarget>('none');

  const fetchAll = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const [m, t] = await Promise.all([apiService.getTaskMeta(), apiService.getTask(id)]);
      setMeta(m);
      setTask(t);
      setTitleDraft(t.title || '');
      setDescHtml(t.description || '');
      if (t.account_id) {
        try {
          const acc = await apiService.getAccount(t.account_id);
          setAccountLink({
            id: acc.id,
            display_name: acc.display_name || '',
            sonic_account_number: acc.sonic_account_number ?? null,
          });
          setAccountSearch(
            formatAccountLinkLabel({
              id: acc.id,
              display_name: acc.display_name || '',
              sonic_account_number: acc.sonic_account_number ?? null,
            })
          );
        } catch {
          setAccountLink({ id: t.account_id, display_name: t.account_id, sonic_account_number: null });
          setAccountSearch(t.account_id);
        }
      } else {
        setAccountLink(null);
        setAccountSearch('');
      }
      if (t.case_id) {
        try {
          const c = await apiService.getCase(t.case_id);
          setCaseLink({
            id: c.id,
            case_number: c.case_number || c.id,
            subject: c.subject || '',
          });
          setCaseSearch(formatCaseLabel({ id: c.id, case_number: c.case_number, subject: c.subject }));
        } catch {
          setCaseLink({ id: t.case_id, case_number: t.case_id, subject: '' });
          setCaseSearch(t.case_id);
        }
      } else {
        setCaseLink(null);
        setCaseSearch('');
      }
      if (t.support_desk_ticket_id) {
        try {
          const tk = await apiService.getSupportDeskTicket(t.support_desk_ticket_id);
          setTicketLink({
            id: tk.id,
            ticket_number: tk.ticket_number || tk.id,
            subject: tk.subject ?? null,
          });
          setTicketSearch(
            formatTicketLabel({
              id: tk.id,
              ticket_number: tk.ticket_number,
              subject: tk.subject,
            })
          );
        } catch {
          setTicketLink({ id: t.support_desk_ticket_id, ticket_number: t.support_desk_ticket_id, subject: null });
          setTicketSearch(t.support_desk_ticket_id);
        }
      } else {
        setTicketLink(null);
        setTicketSearch('');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!task) return;
    setLinkTarget(linkTargetFromTask(task));
  }, [task?.id, task?.account_id, task?.case_id, task?.support_desk_ticket_id]);

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(() => {});
  }, []);

  const accountFieldVisible = linkTarget === 'account' || linkTarget === 'case' || linkTarget === 'ticket';

  useEffect(() => {
    const q = accountSearch.trim();
    if (q.length < 2 || !accountFieldVisible) {
      if (!accountFieldVisible) setAccountOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getAccounts({ q, per_page: 25, page: 1 })
        .then((r: any) => {
          const rows = (r.accounts || []) as any[];
          setAccountOptions(
            rows.map((a) => ({
              id: a.id,
              display_name: a.display_name || '',
              sonic_account_number: a.sonic_account_number ?? null,
            }))
          );
        })
        .catch(() => setAccountOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [accountSearch, accountFieldVisible]);

  useEffect(() => {
    const q = caseSearch.trim();
    if (q.length < 2 || linkTarget !== 'case') {
      if (linkTarget !== 'case') setCaseOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getCases({ q, per_page: 20, page: 1 })
        .then((r) => {
          setCaseOptions(
            (r.cases || []).map((c: any) => ({
              id: c.id,
              case_number: c.case_number || c.id,
              subject: c.subject || '',
            }))
          );
        })
        .catch(() => setCaseOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [caseSearch, linkTarget]);

  useEffect(() => {
    const q = ticketSearch.trim();
    if (q.length < 2 || linkTarget !== 'ticket') {
      if (linkTarget !== 'ticket') setTicketOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getSupportDeskTickets({ q, per_page: 20, page: 1 })
        .then((r) => {
          setTicketOptions(
            (r.tickets || []).map((t: any) => ({
              id: t.id,
              ticket_number: t.ticket_number || t.id,
              subject: t.subject ?? null,
            }))
          );
        })
        .catch(() => setTicketOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [ticketSearch, linkTarget]);

  const statusLabelMap = useMemo(() => new Map((meta?.statuses || []).map((s) => [s.code, s.name])), [meta]);
  const typeLabelMap = useMemo(() => new Map((meta?.types || []).map((t) => [t.code, t.name])), [meta]);

  const patchTask = async (body: Record<string, unknown>, savingKey: string) => {
    if (!id) return;
    try {
      setInlineSaving(savingKey);
      setError('');
      await apiService.updateTask(id, body as any);
      await fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Update failed');
    } finally {
      setInlineSaving(null);
    }
  };

  const persistTitle = async () => {
    if (!task || !id) return;
    const next = titleDraft.trim();
    if (!next || next === task.title) return;
    await patchTask({ title: next }, 'title');
  };

  const persistDescription = async () => {
    if (!task || !id) return;
    const cur = task.description || '';
    if ((descHtml || '') === cur) return;
    await patchTask({ description: descHtml || null }, 'description');
  };

  const handleLinkTargetChange = (_: React.MouseEvent<HTMLElement>, v: LinkTarget | null) => {
    if (v == null || !task) return;
    setLinkTarget(v);
    if (v === 'none' && linkTargetFromTask(task) !== 'none') {
      void patchTask({ account_id: null, case_id: null, support_desk_ticket_id: null }, 'link');
    }
  };

  const linkMismatch = task && linkTarget !== 'none' && linkTarget !== linkTargetFromTask(task);

  const persistAccount = async (opt: AccountLinkOption | null) => {
    if (!id) return;
    await patchTask(
      {
        account_id: opt?.id || null,
        case_id: null,
        support_desk_ticket_id: null,
      },
      'account'
    );
  };

  const persistCase = async (opt: CaseLinkOption | null) => {
    if (!id) return;
    // Don't null account_id — let the backend preserve or auto-derive it from the case.
    await patchTask(
      {
        case_id: opt?.id || null,
        support_desk_ticket_id: null,
      },
      'case'
    );
  };

  const persistTicket = async (opt: TicketLinkOption | null) => {
    if (!id) return;
    // Don't null account_id — let the backend preserve or auto-derive it from the ticket.
    await patchTask(
      {
        support_desk_ticket_id: opt?.id || null,
        case_id: null,
      },
      'ticket'
    );
  };

  const addComment = async () => {
    if (!id || isQuillNoteEmpty(commentHtml)) return;
    try {
      setCommentSaving(true);
      setError('');
      await apiService.addTaskComment(id, { body: commentHtml.trim() });
      setCommentHtml('');
      await fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to add comment');
    } finally {
      setCommentSaving(false);
    }
  };

  const dueValue = useMemo(() => {
    if (!task?.due_at) return null;
    const d = new Date(task.due_at);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [task?.due_at]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !task) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!task) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/crm/tasks')} aria-label="Back to tasks">
          <BackIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          Tasks
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Task
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5 }}>
            <TextField
              label="Title"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void persistTitle()}
              fullWidth
              disabled={inlineSaving === 'title'}
              sx={{ mb: 2 }}
            />

            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Link this task to
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={linkTarget}
              onChange={handleLinkTargetChange}
              sx={{ mb: 1.5, flexWrap: 'wrap' }}
            >
              <ToggleButton value="none">None</ToggleButton>
              <ToggleButton value="account">Customer account</ToggleButton>
              <ToggleButton value="case">Case</ToggleButton>
              <ToggleButton value="ticket">Support ticket</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Link a case or support ticket (not both). A customer account can be set independently
              alongside either — it is auto-filled from the linked case or ticket but can be changed.
            </Typography>
            {linkMismatch && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Select a {linkTarget === 'account' ? 'customer account' : linkTarget === 'case' ? 'case' : 'support ticket'}{' '}
                below to replace the current link.
              </Alert>
            )}

            {linkTarget === 'account' && (
              <Autocomplete
                size="small"
                sx={{ mb: 2 }}
                options={accountOptions}
                value={accountLink}
                inputValue={accountSearch}
                onInputChange={(_, v) => setAccountSearch(v)}
                onChange={(_, newVal) => {
                  setAccountLink(newVal);
                  if (newVal) {
                    setAccountSearch(formatAccountLinkLabel(newVal));
                  } else {
                    setAccountSearch('');
                    setAccountOptions([]);
                  }
                  void persistAccount(newVal);
                }}
                getOptionLabel={(o) => formatAccountLinkLabel(o)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                disabled={inlineSaving === 'account'}
                noOptionsText={
                  accountSearch.trim().length < 2
                    ? 'Type at least 2 characters (e.g. Sonic account number)'
                    : 'No accounts found'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer account"
                    placeholder="Sonic #, name, or email…"
                    helperText="Selecting an account saves immediately."
                  />
                )}
              />
            )}

            {linkTarget === 'case' && (
              <Autocomplete
                size="small"
                sx={{ mb: 2 }}
                options={caseOptions}
                value={caseLink}
                inputValue={caseSearch}
                onInputChange={(_, v) => setCaseSearch(v)}
                onChange={(_, newVal) => {
                  setCaseLink(newVal);
                  if (newVal) setCaseSearch(formatCaseLabel(newVal));
                  else {
                    setCaseSearch('');
                    setCaseOptions([]);
                  }
                  void persistCase(newVal);
                }}
                getOptionLabel={(o) => formatCaseLabel(o)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                disabled={inlineSaving === 'case'}
                noOptionsText={caseSearch.trim().length < 2 ? 'Type at least 2 characters to search cases' : 'No cases found'}
                renderInput={(params) => (
                  <TextField {...params} label="Case" placeholder="Case # or subject…" helperText="Saves on select." />
                )}
              />
            )}

            {linkTarget === 'ticket' && (
              <Autocomplete
                size="small"
                sx={{ mb: 2 }}
                options={ticketOptions}
                value={ticketLink}
                inputValue={ticketSearch}
                onInputChange={(_, v) => setTicketSearch(v)}
                onChange={(_, newVal) => {
                  setTicketLink(newVal);
                  if (newVal) setTicketSearch(formatTicketLabel(newVal));
                  else {
                    setTicketSearch('');
                    setTicketOptions([]);
                  }
                  void persistTicket(newVal);
                }}
                getOptionLabel={(o) => formatTicketLabel(o)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                disabled={inlineSaving === 'ticket'}
                noOptionsText={
                  ticketSearch.trim().length < 2 ? 'Type at least 2 characters to search tickets' : 'No tickets found'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Support ticket"
                    placeholder="Ticket # or subject…"
                    helperText="Saves on select."
                  />
                )}
              />
            )}

            {(linkTarget === 'case' || linkTarget === 'ticket') && (
              <Autocomplete
                size="small"
                sx={{ mb: 2 }}
                options={accountOptions}
                value={accountLink}
                inputValue={accountSearch}
                onInputChange={(_, v) => setAccountSearch(v)}
                onChange={(_, newVal) => {
                  setAccountLink(newVal);
                  if (newVal) {
                    setAccountSearch(formatAccountLinkLabel(newVal));
                  } else {
                    setAccountSearch('');
                    setAccountOptions([]);
                  }
                  void patchTask({ account_id: newVal?.id || null }, 'account');
                }}
                getOptionLabel={(o) => formatAccountLinkLabel(o)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                disabled={inlineSaving === 'account'}
                noOptionsText={
                  accountSearch.trim().length < 2
                    ? 'Type at least 2 characters to search accounts'
                    : 'No accounts found'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer account"
                    placeholder="Sonic #, name, or email…"
                    helperText="Auto-filled from the linked case/ticket. Can be changed independently."
                  />
                )}
              />
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!!inlineSaving}>
                  <InputLabel id="task-status-lbl">Status</InputLabel>
                  <Select
                    labelId="task-status-lbl"
                    label="Status"
                    value={task.status_code || ''}
                    onChange={(e) => void patchTask({ status_code: String(e.target.value) }, 'status')}
                  >
                    {(meta?.statuses || []).map((s) => (
                      <MenuItem key={s.code} value={s.code} disabled={s.is_active === false}>
                        {s.name}
                        {s.is_closed ? ' (terminal)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!!inlineSaving}>
                  <InputLabel id="task-type-lbl">Type</InputLabel>
                  <Select
                    labelId="task-type-lbl"
                    label="Type"
                    value={task.type_code || ''}
                    onChange={(e) => void patchTask({ type_code: String(e.target.value) }, 'type')}
                  >
                    {(meta?.types || []).map((t) => (
                      <MenuItem key={t.code} value={t.code} disabled={t.is_active === false}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
                  <DateTimePicker
                    label="Due"
                    value={dueValue}
                    onAccept={(v) => {
                      void patchTask({ due_at: v ? v.toISOString() : null }, 'due');
                    }}
                    disabled={inlineSaving === 'due'}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true, helperText: 'Confirm in picker to save' },
                      actionBar: { actions: ['clear', 'cancel', 'accept'] },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!!inlineSaving}>
                  <InputLabel id="task-assignee-lbl">Assignee</InputLabel>
                  <Select
                    labelId="task-assignee-lbl"
                    label="Assignee"
                    value={task.assigned_user_id || ''}
                    onChange={(e) =>
                      void patchTask(
                        { assigned_user_id: e.target.value ? String(e.target.value) : null },
                        'assignee'
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.display_name || u.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => void persistDescription()}
                disabled={inlineSaving === 'description' || (descHtml || '') === (task.description || '')}
              >
                Save description
              </Button>
            </Stack>
            <ReactQuill theme="snow" value={descHtml} onChange={setDescHtml} modules={NOTE_QUILL_MODULES} style={{ minHeight: 200 }} />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Activity
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Add comment
            </Typography>
            <ReactQuill
              theme="snow"
              value={commentHtml}
              onChange={setCommentHtml}
              modules={NOTE_QUILL_MODULES}
              style={{ minHeight: 140, marginBottom: 8 }}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button variant="contained" onClick={() => void addComment()} disabled={commentSaving || isQuillNoteEmpty(commentHtml)}>
                Post comment
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.25}>
              {(task.activity || []).map((a) => (
                <Box key={a.id}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(a.created_at).toLocaleString()} · {a.activity_type}
                  </Typography>
                  {a.body &&
                    (looksLikeHtml(a.body) ? (
                      <Box className="task-activity-html" sx={{ '& p': { m: 0 }, fontSize: '0.875rem' }} dangerouslySetInnerHTML={{ __html: a.body }} />
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {a.body}
                      </Typography>
                    ))}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
              {(!task.activity || task.activity.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No activity yet
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, position: { md: 'sticky' }, top: { md: 16 } }}>
            <Typography variant="overline" color="text.secondary">
              Summary
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={statusLabelMap.get(task.status_code || '') || task.status_code || '—'}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip size="small" label={typeLabelMap.get(task.type_code || '') || task.type_code || '—'} variant="outlined" />
                </Box>
              </Box>
              {task.due_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Due
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(task.due_at).toLocaleString()}
                  </Typography>
                </Box>
              )}
              {task.case_id && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Case
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button size="small" component={RouterLink} to={`/crm/cases/${task.case_id}`}>
                      Open case
                    </Button>
                    <IconButton size="small" component={RouterLink} to={`/crm/cases/${task.case_id}`} aria-label="Open case">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              )}
              {task.support_desk_ticket_id && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Support ticket
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button size="small" component={RouterLink} to={`/crm/support-desk/${task.support_desk_ticket_id}`}>
                      Open ticket
                    </Button>
                    <IconButton
                      size="small"
                      component={RouterLink}
                      to={`/crm/support-desk/${task.support_desk_ticket_id}`}
                      aria-label="Open ticket"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              )}
              {task.account_id && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button size="small" component={RouterLink} to={`/crm/accounts/${task.account_id}`}>
                      Open account
                    </Button>
                    <IconButton size="small" component={RouterLink} to={`/crm/accounts/${task.account_id}`} aria-label="Open account">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaskDetailPageV2;
